import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { siteInfo, testimonials } from '@/mocks/mangaData';

const platformValues = [
  { icon: 'ri-smartphone-line', title: '下拉式阅读创新', desc: '首创漫画下拉式连续阅读技术，打破传统翻页模式，让读者享受如瀑布般流畅的阅读体验。' },
  { icon: 'ri-rocket-line', title: '极速加载引擎', desc: '自研 CDN 加速与图片预加载系统，平均加载速度仅 0.3 秒，实现真正的秒开体验。' },
  { icon: 'ri-heart-line', title: '永久免费承诺', desc: '自 2012 年创立以来始终坚持免费阅读，通过品牌合作和广告收入维持运营，让漫画回归大众。' },
  { icon: 'ri-shield-check-line', title: '正版版权合规', desc: '与 680+ 家版权方和漫画家建立合作，所有内容均获正版授权，为创作者提供稳定收入。' },
];

const stats = [
  { value: siteInfo.foundedYear, label: '创立于' },
  { value: siteInfo.totalComics, label: '漫画资源' },
  { value: siteInfo.dailyVisits, label: '日均访问' },
  { value: siteInfo.partners, label: '合作伙伴' },
];

const contactInfo = [
  { icon: 'ri-mail-line', label: '电子邮箱', value: siteInfo.email },
  { icon: 'ri-phone-line', label: '客服电话', value: siteInfo.phone },
  { icon: 'ri-map-pin-line', label: '公司地址', value: siteInfo.address },
];

export default function AboutPage() {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [formStatus, setFormStatus] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = '关于我们 - 4399漫画网';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', '4399漫画网是国内领先的免费漫画下拉式阅读平台，自2012年创立以来，累计收录12万+部漫画，服务350万+日活跃用户。了解我们的品牌故事、平台数据与商务合作信息。');
    const metaKw = document.querySelector('meta[name="keywords"]');
    if (metaKw) metaKw.setAttribute('content', '4399漫画,关于我们,漫画平台,商务合作,品牌介绍');
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', 'http://chigua52.cn/about');
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const honeypot = (e.currentTarget as HTMLFormElement).querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypot && honeypot.value.trim()) {
      setFormStatus({ type: 'success', msg: '提交成功！我们会尽快与您联系。' });
      setFormData({ name: '', email: '', subject: '', message: '' });
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData(e.currentTarget as HTMLFormElement);
      fd.delete('phone_alt');
      const res = await fetch('http://chigua52.cn/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(fd as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(text); } catch { /* ignore */ }
      if (res.ok && (parsed as { code?: string }).code === 'OK') {
        setFormStatus({ type: 'success', msg: '提交成功！我们会尽快与您联系。' });
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        const errMsg = (parsed as { meta?: { message?: string } })?.meta?.message || '提交失败，请稍后重试。';
        setFormStatus({ type: 'error', msg: errMsg });
      }
    } catch {
      setFormStatus({ type: 'error', msg: '网络异常，请稍后重试。' });
    } finally {
      setSubmitting(false);
    }
  };

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: '关于我们' },
  ];

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    'name': '4399漫画网',
    'url': 'http://chigua52.cn/',
    'description': siteInfo.siteDescription,
    'foundingDate': '2012',
    'address': {
      '@type': 'PostalAddress',
      'addressLocality': '广州',
      'addressRegion': '广东',
      'addressCountry': 'CN',
    },
    'contactPoint': {
      '@type': 'ContactPoint',
      'email': siteInfo.email,
      'telephone': siteInfo.phone,
      'contactType': 'business cooperation',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      { '@type': 'ListItem', 'position': 1, 'name': '首页', 'item': 'http://chigua52.cn/' },
      { '@type': 'ListItem', 'position': 2, 'name': '关于我们', 'item': 'http://chigua52.cn/about' },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <Breadcrumb items={breadcrumbItems} />

        <header className="relative w-full h-[300px] md:h-[400px] flex flex-col items-center justify-center mb-0 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20artistic%20illustration%20of%20a%20creative%20team%20workspace%20with%20manga%20and%20comic%20elements%2C%20orange%20coral%20cream%20tones%2C%20cozy%20modern%20office%20atmosphere%2C%20collaborative%20creative%20environment%2C%20soft%20lighting&width=1800&height=600&seq=about-hero&orientation=landscape"
            alt="4399漫画网 - 关于我们"
            title="4399漫画网 - 品牌介绍"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/40"></div>
          <div className="relative z-10 text-center px-4 w-full max-w-2xl mx-auto">
            <h1 className="text-2xl md:text-4xl font-bold text-white font-heading">关于 4399 漫画网</h1>
            <p className="mt-3 text-sm md:text-base text-white/70 max-w-lg mx-auto">
              让漫画阅读更自由 —— 国内领先的免费漫画下拉式阅读平台
            </p>
          </div>
        </header>

        <main>
          <section className="py-12 md:py-20 px-4 md:px-6 bg-secondary-50/80">
            <div className="mx-auto max-w-7xl">
              <div className="text-center mb-10 md:mb-14">
                <span className="inline-block bg-white px-4 py-1.5 rounded-full text-xs font-medium text-foreground-600 border border-background-200/70">
                  品牌使命
                </span>
                <h2 className="mt-4 text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
                  让漫画阅读<br />更自由
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {platformValues.map((val) => (
                  <div key={val.title} className="text-center p-6 bg-white/60 rounded-xl">
                    <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center bg-secondary-100 rounded-xl">
                      <i className={`${val.icon} text-xl text-secondary-600`}></i>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-950 mb-2">{val.title}</h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">{val.desc}</p>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mt-12 text-center">
                {stats.map((stat) => (
                  <div key={stat.label}>
                    <p className="text-3xl md:text-4xl font-black text-secondary-700 font-heading">{stat.value}</p>
                    <p className="text-xs text-foreground-500 mt-1">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-12 md:py-16 px-4 md:px-6 mx-auto max-w-7xl">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading text-center mb-8 md:mb-12">
              媒体与用户评价
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((t) => (
                <div key={t.id} className="bg-foreground-900 rounded-2xl p-6 md:p-8 flex flex-col">
                  <span className="text-4xl text-accent-300 font-serif leading-none mb-3">&ldquo;</span>
                  <blockquote className="text-white/90 text-sm leading-relaxed mb-5 flex-1">
                    {t.content}
                  </blockquote>
                  <div>
                    <p className="text-white text-sm font-semibold">{t.name}</p>
                    <p className="text-white/50 text-xs">{t.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section id="contact" className="py-12 md:py-20 px-4 md:px-6 bg-background-100">
            <div className="mx-auto max-w-7xl">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-4">
                    商务合作与联系我们
                  </h2>
                  <p className="text-sm text-foreground-500 mb-8 leading-relaxed">
                    无论您是版权方、漫画家、品牌广告主还是有其他合作意向，欢迎通过以下方式与我们联系。我们的商务团队将在 24 小时内回复。
                  </p>
                  <div className="space-y-5">
                    {contactInfo.map((info) => (
                      <div key={info.label} className="flex items-start gap-4">
                        <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-xl flex-shrink-0">
                          <i className={`${info.icon} text-primary-500 text-lg`}></i>
                        </div>
                        <div>
                          <p className="text-xs text-foreground-400 mb-0.5">{info.label}</p>
                          <p className="text-sm font-medium text-foreground-950">{info.value}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white rounded-2xl p-6 md:p-8 border border-background-200/70">
                  <h3 className="text-lg font-bold text-foreground-950 font-heading mb-6">发送消息</h3>
                  <form
                    onSubmit={handleSubmit}
                    className="space-y-4"
                  >
                    <input
                      type="text"
                      name="phone_alt"
                      className="form-anti-spam"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                    <div>
                      <label className="block text-xs text-foreground-500 mb-1.5">您的姓名</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        className="w-full bg-background-50 border border-background-200/70 rounded-lg px-4 py-2.5 text-sm text-foreground-950 placeholder-foreground-300 outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-foreground-500 mb-1.5">电子邮箱</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        className="w-full bg-background-50 border border-background-200/70 rounded-lg px-4 py-2.5 text-sm text-foreground-950 placeholder-foreground-300 outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-foreground-500 mb-1.5">合作主题</label>
                      <input
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                        required
                        className="w-full bg-background-50 border border-background-200/70 rounded-lg px-4 py-2.5 text-sm text-foreground-950 placeholder-foreground-300 outline-none focus:border-primary-400 transition-colors"
                        placeholder="请简要描述合作意向"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-foreground-500 mb-1.5">详细说明</label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full bg-background-50 border border-background-200/70 rounded-lg px-4 py-2.5 text-sm text-foreground-950 placeholder-foreground-300 outline-none focus:border-primary-400 transition-colors resize-none"
                        placeholder="请详细描述您的合作需求（限500字）"
                      />
                      <p className="text-xs text-foreground-400 mt-1">{formData.message.length}/500</p>
                    </div>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white py-3 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {submitting ? '提交中...' : '提交合作意向'}
                    </button>
                    {formStatus && (
                      <p className={`text-xs text-center ${formStatus.type === 'success' ? 'text-secondary-600' : 'text-red-500'}`}>
                        {formStatus.msg}
                      </p>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}